import { EventEmitter } from "eventemitter3";
import { difference } from "lodash";
import {
  isInterrupted,
  isModelTurn,
  isServerContentMessage,
  isSetupCompleteMessage,
  isToolCallCancellationMessage,
  isToolCallMessage,
  isTurnComplete,
} from "../multimodal-live-types";
import { blobToJSON, base64ToArrayBuffer } from "./utils";

/**
 * A event-emitting class that manages the connection to the websocket and emits
 * events to the rest of the application.
 * If you dont want to use react you can still use this.
 */
export class MultimodalLiveClient extends EventEmitter {
  constructor({ url, apiKey }) {
    super();
    this.ws = null;
    this.config = null;
    url =
      url ||
      `wss://generativelanguage.googleapis.com/ws/google.ai.generativelanguage.v1alpha.GenerativeService.BidiGenerateContent`;
    url += `?key=${apiKey}`;
    this.url = url;
    this.send = this.send.bind(this);
  }

  getConfig() {
    return { ...this.config };
  }

  log(type, message) {
    const log = {
      date: new Date(),
      type,
      message,
    };
    this.emit("log", log);
  }

  connect(config) {
    this.config = config;

    const ws = new WebSocket(this.url);

    ws.addEventListener("message", async (evt) => {
      if (evt.data instanceof Blob) {
        this.receive(evt.data);
      } else {
        console.log("non blob message", evt);
      }
    });
    return new Promise((resolve, reject) => {
      const onError = (ev) => {
        this.disconnect(ws);
        const message = `Could not connect to "${this.url}"`;
        this.log(`server.${ev.type}`, message);
        reject(new Error(message));
      };
      ws.addEventListener("error", onError);
      ws.addEventListener("open", (ev) => {
        if (!this.config) {
          reject("Invalid config sent to `connect(config)`");
          return;
        }
        this.log(`client.${ev.type}`, `connected to socket`);
        this.emit("open");

        this.ws = ws;

        const setupMessage = {
          setup: this.config,
        };
        this._sendDirect(setupMessage);
        this.log("client.send", "setup");

        ws.removeEventListener("error", onError);
        ws.addEventListener("close", (ev) => {
          console.log(ev);
          this.disconnect(ws);
          let reason = ev.reason || "";
          if (reason.toLowerCase().includes("error")) {
            const prelude = "ERROR]";
            const preludeIndex = reason.indexOf(prelude);
            if (preludeIndex > 0) {
              reason = reason.slice(
                preludeIndex + prelude.length + 1,
                Infinity,
              );
            }
          }
          this.log(
            `server.${ev.type}`,
            `disconnected ${reason ? `with reason: ${reason}` : ``}`,
          );
          this.emit("close", ev);
        });
        resolve(true);
      });
    });
  }

  disconnect(ws) {
    // could be that this is an old websocket and theres already a new instance
    // only close it if its still the correct reference
    if ((!ws || this.ws === ws) && this.ws) {
      this.ws.close();
      this.ws = null;
      this.log("client.close", `Disconnected`);
      return true;
    }
    return false;
  }

  async receive(blob) {
    const response = await blobToJSON(blob);
    if (isToolCallMessage(response)) {
      this.log("server.toolCall", response);
      this.emit("toolcall", response.toolCall);
      return;
    }
    if (isToolCallCancellationMessage(response)) {
      this.log("receive.toolCallCancellation", response);
      this.emit("toolcallcancellation", response.toolCallCancellation);
      return;
    }

    if (isSetupCompleteMessage(response)) {
      this.log("server.send", "setupComplete");
      this.emit("setupcomplete");
      return;
    }

    // this json also might be `contentUpdate { interrupted: true }`
    // or contentUpdate { end_of_turn: true }
    if (isServerContentMessage(response)) {
      const { serverContent } = response;
      if (isInterrupted(serverContent)) {
        this.log("receive.serverContent", "interrupted");
        this.emit("interrupted");
        return;
      }
      if (isTurnComplete(serverContent)) {
        this.log("server.send", "turnComplete");
        this.emit("turncomplete");
        //plausible theres more to the message, continue
      }

      if (isModelTurn(serverContent)) {
        let parts = serverContent.modelTurn.parts;

        // when its audio that is returned for modelTurn
        const audioParts = parts.filter(
          (p) => p.inlineData && p.inlineData.mimeType.startsWith("audio/pcm"),
        );
        const base64s = audioParts.map((p) => p.inlineData?.data);

        // strip the audio parts out of the modelTurn
        if (audioParts.length) {
          const audioPartTypes = audioParts.map((p) => p.inlineData?.mimeType);
          this.log("server.audio", `buffer (${base64s.length})`);
          for (let i = 0; i < base64s.length; i++) {
            const data = base64s[i];
            if (data) {
              const arrayBuffer = base64ToArrayBuffer(data);
              this.emit("audio", arrayBuffer);
            }
          }
          //edit the rest of the parts (minus the audio parts)
          const allowedTypesToContinue = [
            "text/plain",
          ];
          parts = parts.filter(
            (part) =>
              !part.inlineData ||
              allowedTypesToContinue.includes(part.inlineData.mimeType),
          );
          serverContent.modelTurn.parts = parts;
        }

        this.log("receive.content", response);
      }

      this.emit("content", serverContent);
    }
  }

  sendRealtimeInput(chunks) {
    if (!this.ws) {
      this.log("client.realtimeInput", `Cannot send data as socket is closed`);
      console.warn("Cannot send data as socket is closed");
      return;
    }

    const realtimeInput = {
      realtimeInput: {
        mediaChunks: chunks,
      },
    };

    this._sendDirect(realtimeInput);
    this.log("client.realtimeInput", chunks[0].mimeType.split("/")[0]);
  }

  sendToolResponse(toolResponse) {
    if (!this.ws) {
      this.log(
        "client.toolResponse",
        `Cannot send data as socket is closed: ${JSON.stringify(toolResponse)}`,
      );
      return;
    }
    this._sendDirect({ toolResponse });
    this.log("client.toolResponse", toolResponse);
  }

  send(parts, turnComplete = true) {
    if (!this.ws) {
      this.log("client.send", `Cannot send data as socket is closed`);
      return;
    }
    if (!Array.isArray(parts)) {
      parts = [parts];
    }

    const message = {
      clientContent: {
        turns: [
          {
            role: "USER",
            parts,
          },
        ],
        turnComplete,
      },
    };

    this._sendDirect(message);
    this.log("client.send", message);
  }

  _sendDirect(request) {
    this.ws?.send(JSON.stringify(request));
  }
} 